import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getStoredRefreshToken, getStoredToken, persistRefreshToken, persistToken } from '@/services/http'
import type { AuthTokenPayload } from '@/types/api'
import type { AuthUser } from '@/types/api'

const USER_STORAGE_KEY = 'auth.user'

function getStoredUser() {
  const raw = localStorage.getItem(USER_STORAGE_KEY)
  return raw ? (JSON.parse(raw) as AuthUser) : null
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(getStoredUser())
  const token = ref<string | null>(getStoredToken())
  const refreshToken = ref<string | null>(getStoredRefreshToken())
  const isAuthenticated = computed(() => Boolean(token.value))
  const role = computed(() => user.value?.role ?? null)

  function setToken(nextToken: string | null) {
    token.value = nextToken
    persistToken(nextToken)
  }

  function setRefreshToken(nextToken: string | null) {
    refreshToken.value = nextToken
    persistRefreshToken(nextToken)
  }

  function setUser(nextUser: AuthUser | null) {
    user.value = nextUser
    if (nextUser) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser))
    } else {
      localStorage.removeItem(USER_STORAGE_KEY)
    }
  }

  function setSession(payload: AuthTokenPayload) {
    setToken(payload.access_token)
    setRefreshToken(payload.refresh_token)
    setUser(payload.user)
  }

  function clearSession() {
    setUser(null)
    setToken(null)
    setRefreshToken(null)
  }

  function hasAnyRole(roles: AuthUser['role'][]) {
    return Boolean(user.value && roles.includes(user.value.role))
  }

  return {
    user,
    token,
    refreshToken,
    isAuthenticated,
    role,
    setToken,
    setRefreshToken,
    setUser,
    setSession,
    hasAnyRole,
    clearSession
  }
})
